import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { apiErrorCode, apiErrorMessage } from "@/api/client";
import { getCompany } from "@/api/endpoints";
import { DataState } from "@/components/data-state";
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Textarea,
} from "@/components/ui/primitives";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import {
  companySearchText,
  personSearchText,
  productSearchText,
  sampleSearchText,
} from "@/lib/search-index";
import { created, deleted, edited, push } from "@/lib/sync";
import type {
  CompanyDetail,
  CompanyLocation,
  CompanyNote,
  ContactChannel,
  Person,
  PushChanges,
} from "@/types";

/**
 * One company, and the only page in the panel that writes organisation data.
 *
 * Everything it saves goes through `/sync/push` (see `lib/sync.ts`), which
 * means this page carries two obligations the server will not remind it of:
 *
 *  - **`searchText` is the writer's job.** A record saved without it, or with
 *    it built from the wrong parts, still saves and still displays — and then
 *    cannot be found again by anybody, on any device.
 *  - **A company's search text spans its children**, so touching a person, a
 *    phone number or an address rewrites the company row as well.
 */

/** Plans that may write. `readonly` and `purged` keep reading; free never had
 *  anything up here to begin with. */
const WRITABLE_PLANS = ["premium", "grace"];

type Children = {
  locations: CompanyLocation[];
  channels: ContactChannel[];
  persons: Person[];
  notes: CompanyNote[];
};

export function CompanyDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { t } = useI18n();
  const { activeOrgId, activeOrg, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["company", activeOrgId, id],
    queryFn: () => getCompany(id),
  });

  // The server enforces both of these; hiding the controls only spares
  // somebody a form they would not be allowed to submit.
  const canWrite = role === "admin" && WRITABLE_PLANS.includes(activeOrg?.plan ?? "free");

  function describe(caught: unknown) {
    const code = apiErrorCode(caught);
    if (code === "PLAN_REQUIRED") return t("planRequiredWrite");
    if (code === "FORBIDDEN") return t("onlyAdmins");
    return apiErrorMessage(caught, t("networkError"));
  }

  const save = useMutation({
    mutationFn: (changes: PushChanges) => push(changes),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["company", activeOrgId, id] });
      // Prefix match: the list's key carries the search term and page too.
      void queryClient.invalidateQueries({ queryKey: ["companies", activeOrgId] });
      void queryClient.invalidateQueries({ queryKey: ["overview", activeOrgId] });
    },
    onError: (caught) => setError(describe(caught)),
  });

  const data = detail.data;
  /**
   * The server may be a deploy behind this page — a rollback, or a panel that
   * shipped first. Reading `notes` straight off the response would then throw
   * during render and take the whole company page down over a section that is
   * merely empty.
   */
  const notes = data?.notes ?? [];

  /**
   * A company write, with its search text rebuilt from the children it will
   * have once `changes` lands.
   */
  function commitCompany(fields: Record<string, unknown>, children: Children) {
    if (!data) return;
    const next = { ...data.company, ...fields };
    const changes: PushChanges = {
      companies: [edited(data.company, { ...fields, searchText: companySearchText(next, children) })],
    };

    // A rename fans out: products and samples carry the company's name inside
    // their own search text, so "acme vida" finds the product without a join.
    // Skipping this leaves every one of them findable only under the old name.
    if (typeof fields.name === "string" && fields.name !== data.company.name) {
      const name = fields.name;
      changes.products = data.products.map((row) =>
        edited(row, { searchText: productSearchText(row, name) })
      );
      changes.samples = data.samples.map((row) =>
        edited(row, { searchText: sampleSearchText(row, name) })
      );
    }

    save.mutate(changes);
  }

  /** A child write, plus the company row its search text lives in. */
  function commitChild(
    collection: "locations" | "contactChannels" | "persons",
    record: ReturnType<typeof edited>,
    children: Children
  ) {
    if (!data) return;
    save.mutate({
      [collection]: [record],
      companies: [
        edited(data.company, { searchText: companySearchText(data.company, children) }),
      ],
    });
  }

  /** Replaces a row in a list, or appends it when it is new. */
  function withRow<T extends { _id: string }>(rows: T[], row: T): T[] {
    return rows.some((existing) => existing._id === row._id)
      ? rows.map((existing) => (existing._id === row._id ? row : existing))
      : [...rows, row];
  }

  function deleteCompany() {
    if (!data) return;
    if (!window.confirm(t("companyDeleteWarning"))) return;

    // The same cascade the app performs. Every one of these has to travel as
    // its own tombstone: a record that is merely orphaned goes on existing on
    // every phone that already pulled it.
    save.mutate(
      {
        companies: [deleted(data.company)],
        locations: data.locations.map(deleted),
        contactChannels: data.channels.map(deleted),
        persons: data.persons.map(deleted),
        products: data.products.map(deleted),
        samples: data.samples.map(deleted),
        certificates: data.certificates.map(deleted),
        contracts: data.contracts.map(deleted),
        companyNotes: notes.map(deleted),
        attachments: data.attachments.map(deleted),
      },
      { onSuccess: () => navigate("/companies") }
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Link to="/companies">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="size-4" />
            {t("navCompanies")}
          </Button>
        </Link>
      </div>

      <DataState
        isLoading={detail.isLoading}
        isError={detail.isError}
        isEmpty={false}
        onRetry={() => void detail.refetch()}
      >
        {data && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <PageTitle>{data.company.name}</PageTitle>
              {data.company.isIndividual && <Badge tone="neutral">{t("individual")}</Badge>}
              {data.company.rating != null && (
                <Badge tone="primary">{data.company.rating} ★</Badge>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <CompanyForm
              detail={data}
              canWrite={canWrite}
              busy={save.isPending}
              onSave={(fields) =>
                commitCompany(fields, {
                  locations: data.locations,
                  channels: data.channels,
                  persons: data.persons,
                  notes,
                })
              }
              onDelete={deleteCompany}
            />

            <RecordSection<Person>
              title={t("people")}
              rows={data.persons}
              canWrite={canWrite}
              busy={save.isPending}
              fields={[
                { key: "name", label: t("personName"), required: true },
                { key: "title", label: t("personTitle") },
                { key: "phone", label: t("cardPhone") },
                { key: "email", label: t("cardEmail") },
                { key: "notes", label: t("notes"), multiline: true },
              ]}
              summary={(row) => (
                <>
                  <p className="truncate font-medium">{row.name}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    {[row.title, row.phone, row.email].filter(Boolean).join(" · ")}
                  </p>
                </>
              )}
              onSave={(draft, existing) => {
                // Spelled out rather than cast: the draft is a bag of strings
                // keyed by the field spec above, and a typo there would
                // silently drop a field out of the search text.
                const fields = {
                  ...draft,
                  searchText: personSearchText({
                    name: draft.name ?? "",
                    title: draft.title ?? "",
                    phone: draft.phone ?? "",
                    email: draft.email ?? "",
                    notes: draft.notes ?? "",
                  }),
                };
                const record = existing
                  ? edited(existing, fields)
                  : created({ ...fields, companyId: id });
                commitChild("persons", record, {
                  locations: data.locations,
                  channels: data.channels,
                  persons: withRow(data.persons, record as unknown as Person),
                  notes,
                });
              }}
              onDelete={(row) =>
                commitChild("persons", deleted(row), {
                  locations: data.locations,
                  channels: data.channels,
                  persons: data.persons.filter((person) => person._id !== row._id),
                  notes,
                })
              }
            />

            <RecordSection<ContactChannel>
              title={t("contact")}
              rows={data.channels}
              canWrite={canWrite}
              busy={save.isPending}
              fields={[{ key: "value", label: t("channelValue"), required: true }]}
              summary={(row) => <p className="truncate font-medium">{row.value}</p>}
              onSave={(draft, existing) => {
                const record = existing
                  ? edited(existing, draft)
                  : created({ ...draft, companyId: id });
                commitChild("contactChannels", record, {
                  locations: data.locations,
                  channels: withRow(data.channels, record as unknown as ContactChannel),
                  persons: data.persons,
                  notes,
                });
              }}
              onDelete={(row) =>
                commitChild("contactChannels", deleted(row), {
                  locations: data.locations,
                  channels: data.channels.filter((channel) => channel._id !== row._id),
                  persons: data.persons,
                  notes,
                })
              }
            />

            <RecordSection<CompanyLocation>
              title={t("locations")}
              rows={data.locations}
              canWrite={canWrite}
              busy={save.isPending}
              fields={[
                { key: "address", label: t("address"), multiline: true },
                { key: "city", label: t("cardCity") },
                { key: "country", label: t("cardCountry") },
              ]}
              summary={(row) => (
                <>
                  <p className="truncate font-medium">
                    {[row.city, row.country].filter(Boolean).join(", ") || t("address")}
                  </p>
                  {row.address && (
                    <p className="truncate text-sm text-muted-foreground">{row.address}</p>
                  )}
                </>
              )}
              onSave={(draft, existing) => {
                const record = existing
                  ? edited(existing, draft)
                  : created({ ...draft, companyId: id });
                commitChild("locations", record, {
                  locations: withRow(data.locations, record as unknown as CompanyLocation),
                  channels: data.channels,
                  persons: data.persons,
                  notes,
                });
              }}
              onDelete={(row) =>
                commitChild("locations", deleted(row), {
                  locations: data.locations.filter((location) => location._id !== row._id),
                  channels: data.channels,
                  persons: data.persons,
                  notes,
                })
              }
            />

            <ReadOnlySection title={t("products")} names={data.products.map((row) => row.name)} />
            <ReadOnlySection title={t("samples")} names={data.samples.map((row) => row.name)} />
            <ReadOnlySection
              title={t("certificates")}
              names={data.certificates.map((row) => row.name)}
            />
            <ReadOnlySection
              title={t("contracts")}
              names={data.contracts.map((row) => row.title)}
            />
            <ReadOnlySection
              title={t("notes")}
              names={notes.map(
                (row) => row.title.trim() || row.body.trim().split("\n")[0] || ""
              )}
            />
          </div>
        )}
      </DataState>
    </div>
  );
}

/** The company's own fields. Everything else on the page hangs off it. */
function CompanyForm({
  detail,
  canWrite,
  busy,
  onSave,
  onDelete,
}: {
  detail: CompanyDetail;
  canWrite: boolean;
  busy: boolean;
  onSave: (fields: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(detail.company.name);
  const [notes, setNotes] = useState(detail.company.notes);
  const [rating, setRating] = useState(
    detail.company.rating == null ? "" : String(detail.company.rating)
  );

  if (!editing) {
    return (
      <Card className="space-y-3 p-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium">{t("about")}</p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {detail.company.notes || "—"}
            </p>
          </div>
          {canWrite && (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="size-4" />
                {t("edit")}
              </Button>
              <Button variant="destructive" size="sm" onClick={onDelete} disabled={busy}>
                <Trash2 className="size-4" />
                {t("deleteAction")}
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className="space-y-3 p-4">
      <Field label={t("companyName")} htmlFor="company-name">
        <Input
          id="company-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field
        label={t("rating")}
        htmlFor="company-rating"
        hint={t("ratingHint")}
      >
        <Input
          id="company-rating"
          type="number"
          min={1}
          max={5}
          value={rating}
          onChange={(event) => setRating(event.target.value)}
        />
      </Field>
      <Field label={t("about")} htmlFor="company-notes">
        <Textarea
          id="company-notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </Field>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={busy || name.trim() === ""}
          onClick={() => {
            onSave({
              name: name.trim(),
              notes,
              // Empty means "not rated", which is a value the field carries —
              // not a field to leave out, since the server would then keep the
              // old rating.
              rating: rating.trim() === "" ? null : Number(rating),
            });
            setEditing(false);
          }}
        >
          {t("save")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setName(detail.company.name);
            setNotes(detail.company.notes);
            setRating(detail.company.rating == null ? "" : String(detail.company.rating));
            setEditing(false);
          }}
        >
          {t("cancel")}
        </Button>
      </div>
    </Card>
  );
}

type FieldSpec<T> = {
  key: Extract<keyof T, string>;
  label: string;
  required?: boolean;
  multiline?: boolean;
};

/**
 * A list of one company's child records, with an inline form.
 *
 * People, phone numbers and addresses differ only in which fields they carry,
 * so the add/edit/delete choreography lives here once. Anything with a
 * genuinely different shape should be its own component rather than gain a
 * flag.
 */
function RecordSection<T extends { _id: string }>({
  title,
  rows,
  fields,
  summary,
  canWrite,
  busy,
  onSave,
  onDelete,
}: {
  title: string;
  rows: T[];
  fields: FieldSpec<T>[];
  summary: (row: T) => React.ReactNode;
  canWrite: boolean;
  busy: boolean;
  onSave: (draft: Record<string, string>, existing: T | null) => void;
  onDelete: (row: T) => void;
}) {
  const { t } = useI18n();
  // `null` is nothing open; `""` is the blank form for a new record.
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  function open(row: T | null) {
    setOpenId(row ? row._id : "");
    setDraft(
      Object.fromEntries(
        fields.map((field) => [field.key, row ? String(row[field.key] ?? "") : ""])
      )
    );
  }

  const required = fields.filter((field) => field.required);
  const complete = required.every((field) => (draft[field.key] ?? "").trim() !== "");

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        {canWrite && openId === null && (
          <Button variant="secondary" size="sm" onClick={() => open(null)}>
            <Plus className="size-4" />
            {t("add")}
          </Button>
        )}
      </div>

      <Card className="divide-y divide-border">
        {rows.length === 0 && openId !== "" && (
          <p className="p-3 text-sm text-muted-foreground">{t("empty")}</p>
        )}

        {rows.map((row) =>
          openId === row._id ? (
            <div key={row._id} className="space-y-3 p-3">
              <DraftFields fields={fields} draft={draft} setDraft={setDraft} />
              <FormActions
                busy={busy}
                complete={complete}
                onSave={() => {
                  onSave(draft, row);
                  setOpenId(null);
                }}
                onCancel={() => setOpenId(null)}
              />
            </div>
          ) : (
            <div key={row._id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">{summary(row)}</div>
              {canWrite && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => open(row)}>
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(t("confirmDelete"))) onDelete(row);
                    }}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              )}
            </div>
          )
        )}

        {openId === "" && (
          <div className="space-y-3 p-3">
            <DraftFields fields={fields} draft={draft} setDraft={setDraft} />
            <FormActions
              busy={busy}
              complete={complete}
              onSave={() => {
                onSave(draft, null);
                setOpenId(null);
              }}
              onCancel={() => setOpenId(null)}
            />
          </div>
        )}
      </Card>
    </section>
  );
}

function DraftFields<T>({
  fields,
  draft,
  setDraft,
}: {
  fields: FieldSpec<T>[];
  draft: Record<string, string>;
  setDraft: (next: Record<string, string>) => void;
}) {
  return (
    <>
      {fields.map((field) => (
        <Field key={field.key} label={field.label} htmlFor={`draft-${field.key}`}>
          {field.multiline ? (
            <Textarea
              id={`draft-${field.key}`}
              value={draft[field.key] ?? ""}
              onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
            />
          ) : (
            <Input
              id={`draft-${field.key}`}
              value={draft[field.key] ?? ""}
              onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
            />
          )}
        </Field>
      ))}
    </>
  );
}

function FormActions({
  busy,
  complete,
  onSave,
  onCancel,
}: {
  busy: boolean;
  complete: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex gap-2">
      <Button size="sm" disabled={busy || !complete} onClick={onSave}>
        {t("save")}
      </Button>
      <Button variant="ghost" size="sm" onClick={onCancel}>
        {t("cancel")}
      </Button>
    </div>
  );
}

/**
 * What hangs off the company but cannot be edited here yet.
 *
 * Shown rather than hidden: somebody deciding whether to delete a company
 * needs to see that eleven products go with it.
 */
function ReadOnlySection({ title, names }: { title: string; names: string[] }) {
  const { t } = useI18n();
  if (names.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold">
        {title} <span className="text-muted-foreground">({names.length})</span>
      </h2>
      <Card className="divide-y divide-border">
        {names.map((name, index) => (
          <p key={index} className="truncate p-3 text-sm">
            {name || t("empty")}
          </p>
        ))}
      </Card>
    </section>
  );
}
